<template>
  <div>
    <LoadingState v-if="siteStatus.isLoading" :size="50" />
    <ErrorState v-else-if="siteStatus.isErrored" />
    <BackupRestoreNotice v-else-if="backupStatus.isLoading && siteStatus.isLoaded" :site="site" />
    <div v-else-if="siteStatus.isLoaded || siteStatus.isUpdating">
      <PageTitle v-if="routeName.toString() === 'plan-site-detail'">
        <div class="d-flex">
          <div class="screenshot d-none d-md-block mr-4">
            <div class="image">
              <img :src="site.screenshotUrl" class="img-fluid" />
            </div>
          </div>
          <div class="flex-fill">
            <div class="d-flex align-items-center">
              <div class="name mr-2">{{ getSite() }}</div>
              <Logo v-if="getApp()" :logo="getApp()" :size="LOGO_SIZE" />
            </div>
            <div class="d-md-inline-flex mt-4 align-items-baseline">
              <HorizontalList :items="siteItems()" />
            </div>
          </div>
        </div>
      </PageTitle>
      <router-view :key="routePath" />
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import BackupRestoreNotice from "@/components/display/cards/BackupRestoreNotice";
import ErrorState from "@/components/display/ErrorState";
import HorizontalList from "@/components/display/HorizontalList";
import LoadingState from "@/components/display/LoadingState";
import Logo from "@/components/display/Logo";
import PageTitle from "@/components/display/PageTitle";
import store from "@/store";

export default {
  name: "PlanSitePage",
  components: {
    ErrorState,
    HorizontalList,
    LoadingState,
    BackupRestoreNotice,
    Logo,
    PageTitle,
  },
  props: {
    siteId: Number,
  },
  setup(props, { root }) {
    const routePath = computed(() => {
      return root.$route.fullPath;
    });

    const site = computed(() => store.getters["sites/getItem"]);
    const siteStatus = computed(() => store.getters["sites/getItemStatus"]);
    const backupStatus = computed(() => store.getters["sites/getRestoreStatus"]);

    const routeName = computed(
      () => root.$route.matched[root.$route.matched.length - 1].parent.name
    );

    const LOGO_SIZE = 30;

    onMounted(() => {
      store.dispatch("sites/get", props.siteId);
      store.dispatch("sites/getRestoreStatus", props.siteId);
    });

    const getSite = () => site.value.domain;
    const getIp = () => site.value.ip;
    const getTempDomain = () => site.value.tempDomain;
    const getApp = () => site.value.app.type;
    const getAppName = () => site.value.app.name;

    const siteItems = () => [
      {
        key: root.$t("global.type"),
        value: getAppName(),
      },
      {
        key: root.$t("global.temp-domain"),
        value: `<a href="https://${getTempDomain()}" target="_blank" rel="noopener noreferrer">${getTempDomain()}</a>`,
      },
      {
        key: root.$t("global.ip-address"),
        value: getIp(),
        canCopy: true,
      },
    ];

    return {
      getApp,
      getAppName,
      getSite,
      getIp,
      getTempDomain,
      routePath,
      site,
      siteStatus,
      backupStatus,
      LOGO_SIZE,
      siteItems,
      routeName,
    };
  },
};
</script>
<style lang="scss" scoped>
@import "~@/styles/variables";
.screenshot {
  width: 110px;
  .image {
    min-width: 110px;
    height: 80px;
    width: 100%;
    overflow: hidden;
    background-image: url("data:image/svg+xml;base64,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");
    background-position: 50% 50%;
    background-repeat: no-repeat;
    @media (max-width: 546px) and (min-width: 0px) {
      display: none;
    }
  }
}
</style>
